import { useState } from "react";
import { AppShell } from "../components/layout/AppShell";
import { DataTable, type Column } from "../components/ui/DataTable";
import { Badge } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { Modal } from "../components/ui/Modal";
import { Input } from "../components/ui/Fields";
import { usePagedList } from "../hooks/usePagedList";
import { apiRequest, ApiError } from "../api/client";
import { useToast } from "../components/ui/Toast";
import type { RoomThemeCategory } from "../api/types";

/**
 * Round 45I — Room Background theme categories. Each visible category is a
 * tab at the top of the app's Background screen; Visible OFF removes that
 * tab (and its themes) from the app immediately.
 */
export function RoomThemeCategoriesPage() {
  const { rows, isLoading, error, page, setPage, hasNextPage, reload } =
    usePagedList<RoomThemeCategory>("/admin/room-theme-categories");
  const [editing, setEditing] = useState<Partial<RoomThemeCategory> | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();

  async function save() {
    if (!editing?.name?.trim()) {
      showToast("Name is required", "error");
      return;
    }
    setIsSubmitting(true);
    try {
      const body = {
        name: editing.name.trim(),
        is_visible: editing.is_visible ?? true,
        sort_order: Number(editing.sort_order) || 0,
      };
      if (editing.id) await apiRequest(`/admin/room-theme-categories/${editing.id}`, { method: "PATCH", body });
      else await apiRequest("/admin/room-theme-categories", { method: "POST", body });
      showToast("Saved", "success");
      reload();
      setEditing(null);
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not save category", "error");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function toggleVisible(row: RoomThemeCategory) {
    try {
      await apiRequest(`/admin/room-theme-categories/${row.id}`, { method: "PATCH", body: { is_visible: !row.is_visible } });
      showToast(row.is_visible ? "Hidden in the app" : "Visible in the app", "success");
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not update category", "error");
    }
  }

  const columns: Column<RoomThemeCategory>[] = [
    { header: "Name", render: (row) => <p className="font-medium text-ink">{row.name}</p> },
    { header: "Sort Order", render: (row) => row.sort_order },
    {
      header: "Visible",
      render: (row) => (row.is_visible ? <Badge tone="success">ON</Badge> : <Badge tone="neutral">OFF</Badge>),
    },
    {
      header: "",
      className: "text-right",
      render: (row) => (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="secondary" onClick={() => setEditing(row)}>Edit</Button>
          <Button size="sm" variant={row.is_visible ? "danger" : "secondary"} onClick={() => toggleVisible(row)}>
            {row.is_visible ? "Turn OFF" : "Turn ON"}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <AppShell title="Theme Categories">
      <p className="mb-4 max-w-xl text-xs text-ink-faint">
        Each visible category is a tab at the top of the app's Background screen (System / Live / My style). Turning
        Visible OFF hides the tab in the app. Assign themes to a category from Room Themes.
      </p>
      <div className="mb-4 flex justify-end">
        <Button onClick={() => setEditing({ is_visible: true, sort_order: 0 })}>+ Add Category</Button>
      </div>
      <DataTable
        columns={columns}
        rows={rows}
        keyField={(r) => r.id}
        isLoading={isLoading}
        error={error}
        onRetry={reload}
        emptyTitle="No theme categories yet"
        pagination={{ page, pageSize: 20, hasNextPage, onPageChange: setPage }}
      />
      <Modal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? "Edit Category" : "Add Category"}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} isLoading={isSubmitting}>Save</Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input label="Tab name" value={editing?.name ?? ""} onChange={(e) => setEditing((p) => ({ ...p, name: e.target.value }))} />
          <Input
            label="Sort order"
            type="number"
            value={editing?.sort_order ?? 0}
            onChange={(e) => setEditing((p) => ({ ...p, sort_order: Number(e.target.value) }))}
          />
          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={editing?.is_visible ?? true}
              onChange={(e) => setEditing((p) => ({ ...p, is_visible: e.target.checked }))}
            />
            Visible in the app
          </label>
        </div>
      </Modal>
    </AppShell>
  );
}
